import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useContent } from "@/hooks/useContent";
import api from "@/lib/api";
import { BeWomanLogo } from "@/components/BeWomanLogo";
import { ChevronLeft } from "lucide-react";
import { PillarIcon } from "@/components/PillarIcon";

export function Test() {
  const { data: content, isLoading } = useContent();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  if (isLoading || !content) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#00000000]">
        <div className="w-10 h-10 rounded-full border-4 border-[#6B2247] border-t-[#E32C8C] animate-spin" />
      </div>
    );
  }

  const questions = content.questions;
  const q = questions[step];
  const pillar = content.pillars[q.pillar];
  const progress = ((step) / questions.length) * 100;

  const choose = async (score) => {
    const next = [...answers];
    next[step] = score;
    setAnswers(next);
    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 180);
    } else {
      setSubmitting(true);
      try {
        const { data } = await api.post("/test/submit", { answers: next });
        // Update caches so Home/Profilo see the completed test immediately (avoids redirect loop)
        qc.setQueryData(["profile"], { has_test: true, ...data });
        await qc.invalidateQueries({ queryKey: ["profile"] });
        await qc.invalidateQueries({ queryKey: ["state"] });
        navigate("/result", { replace: true });
      } catch {
        setSubmitting(false);
      }
    }
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
    else navigate(-1);
  };

  return (
    <div className="min-h-[100dvh] bg-[#00000000]">
      <div className="max-w-md mx-auto min-h-[100dvh] px-6 py-6 flex flex-col">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={back} data-testid="test-back-btn" className="text-[#EAC6D9]">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <div className="flex justify-between text-xs font-semibold text-[#EAC6D9] mb-1.5">
              <span data-testid="test-progress-label">{step + 1} di {questions.length}</span>
              <span>{Math.round(((step + 1) / questions.length) * 100)}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-[#6B2247] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#E32C8C] to-[#8E1A52] rounded-full transition-all duration-500"
                style={{ width: `${Math.max(progress, 4)}%` }}
              />
            </div>
          </div>
        </div>

        <div key={step} className="flex-1 flex flex-col bw-fade">
          <div
            className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full mb-5"
            style={{ backgroundColor: `${pillar.color}1A`, color: pillar.color }}
          >
            <PillarIcon pillar={q.pillar} className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-wide">{pillar.name}</span>
          </div>

          <h2 className="font-serif text-3xl font-semibold text-[#F6DCEA] leading-tight mb-7" data-testid="test-question">
            {q.text}
          </h2>

          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const selected = answers[step] === i;
              return (
                <button
                  key={i}
                  onClick={() => choose(i)}
                  disabled={submitting}
                  data-testid={`test-option-${i}`}
                  className="w-full text-left px-5 py-4 rounded-2xl border-2 transition-all active:scale-[0.99] disabled:opacity-60"
                  style={{
                    borderColor: selected ? pillar.color : "#6B2247",
                    backgroundColor: selected ? `${pillar.color}14` : "#2A0A1B",
                    color: "#F6DCEA",
                  }}
                >
                  <span className="text-[15px] leading-snug font-medium">{opt}</span>
                </button>
              );
            })}
          </div>
        </div>

        {submitting && (
          <p className="text-center text-sm text-[#EAC6D9] mt-4">Sto costruendo il tuo profilo…</p>
        )}
      </div>
    </div>
  );
}
